import React from 'react';
import ReactPlayer from 'react-player';
import { VideoItemReference } from 'types/sharedComponents';
import { ReactPlayerProps } from 'react-player/types';

export type VideoPlayerClassNames = {
  wrapper: string;
  player: string;
};

export type VideoPreviewClassNames = {
  wrapper: string;
  blur: string;
  portraitOverlay: string;
  overlay: string;
  image: string;
  label: string;
};

export enum VideoPlayerState {
  Init = 'init',
  Starting = 'starting',
  Playing = 'playing',
  Paused = 'paused',
  Ended = `ended`,
}

// These are the providers that are supported by ReactPlayer.
// https://github.com/cookpete/react-player/tree/v2.16.0/src/players
export enum VideoPlayerProvider {
  Unknown = 'unknown',
  YouTube = 'youtube',
  TikTok = 'tiktok',
  HTML5 = 'html5',
}

// ReactPlayer instance with optional helpers exposed by the library
export interface ReactPlayerInstance extends React.ComponentRef<typeof ReactPlayer> {
  getInternalPlayer?: (
    key?: 'youtube' | 'tiktok',
  ) =>
    | { pauseVideo: () => void; stopVideo: () => void; getIframe?: () => HTMLIFrameElement | null }
    | { pause: () => void; stop: () => void; getIframe?: () => HTMLIFrameElement | null }
    | HTMLVideoElement
    | HTMLIFrameElement
    | null;
  getCurrentTime?: () => number;
  getDuration?: () => number;
}

// State object passed to VideoPreview and other components
export interface VideoPlayerStateObject {
  playerState: VideoPlayerState;
  testId: string;
  video: VideoItemReference;
  isPlaying: boolean;
  handlePlayClick: () => void;
  handlePauseClick: () => void;
}

export interface VideoPlayerProps {
  video: VideoItemReference;
  classNames?: Partial<VideoPlayerClassNames>;
  autoPlay?: boolean;
  muted?: boolean;
  rounded?: boolean;
  loop?: boolean;
  hideNativeControls?: boolean;
  playInBackground?: boolean;
  testId?: string;
  onReady?: (currentPlayerId: string) => void;
  onStart?: (currentPlayerId: string) => void;
  onPlay?: (currentPlayerId: string) => void;
  onPause?: (currentPlayerId: string) => void;
  onEnded?: (currentPlayerId: string) => void;
  children?: React.ReactNode;
}

export interface VideoPreviewProps {
  image?: {
    url: string;
    title?: string;
    description?: string;
    width?: number;
    height?: number;
  };
  label?: string;
  showPreviewOnEnd?: boolean;
  priority?: boolean;
  classNames?: Partial<VideoPreviewClassNames>;
  videoPlayerState?: VideoPlayerStateObject;
}

export interface VideoControlsProps {
  position?: 'bottom-right' | 'center';
  className?: string;
  hideControls?: boolean;
  showOnVideoEnd?: boolean;
  hideOnVideoStarted?: boolean;
  videoPlayerState?: VideoPlayerStateObject;
}

export interface ReactPlayerConfig extends Omit<ReactPlayerProps, 'src'> {
  testId: string;
  src: string;
}

export interface CreateReactPlayerConfigInput {
  src: string;
  playing: boolean;
  loop: boolean;
  muted: boolean;
  controls: boolean;
  className: string;
  testId: string;
  onLoadStart?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  ref?: React.Ref<unknown>;
}
